import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import type { DatesSetArg, EventClickArg } from '@fullcalendar/core';
import { useNavigate } from 'react-router-dom';
import PageWrapper from '@/components/layout/PageWrapper';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { calendarApi, repairRequestsApi } from '@/api';
import { toast } from '@/hooks/use-toast';
import type { CalendarEventType } from '@/types';

const EVENT_COLORS: Record<CalendarEventType, string> = {
  repair: '#c9a84c', // muted yellow
  pm_due: '#f97316',
  pm_done: '#22c55e',
};

const STATE_COLORS = {
  overdue: '#ef4444',
  locked: '#6366f1', // repair order created — indigo
  done: '#22c55e',
};

interface SelectedPmEvent {
  id: string;
  title: string;
  start: string;
  type: CalendarEventType;
  equipmentId: number | null;
  assignmentId: number | null;
  pmDescription: string | null;
  requestId: number | null;
}

function useIsMobile() {
  return typeof window !== 'undefined' && window.innerWidth < 768;
}

export default function Calendar() {
  const calendarRef = useRef<FullCalendar>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      .toISOString()
      .slice(0, 10),
    end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
      .toISOString()
      .slice(0, 10),
  });

  const [selectedEvent, setSelectedEvent] = useState<SelectedPmEvent | null>(null);

  const isMobile = useIsMobile();

  const { data: events, isLoading } = useQuery({
    queryKey: ['calendar', dateRange.start, dateRange.end],
    queryFn: () => calendarApi.get(dateRange.start, dateRange.end),
  });

  const createRequestMutation = useMutation({
    mutationFn: (equipmentId: number) =>
      repairRequestsApi.create({
        equipmentId,
        pmAssignmentId: selectedEvent?.assignmentId ?? undefined,
        pmDueDate: selectedEvent?.start ?? undefined,
        autoGenerated: true,
        problem: selectedEvent?.pmDescription
          ? selectedEvent.pmDescription
          : selectedEvent
          ? `PM Due: ${selectedEvent.title}`
          : 'PM service required',
        status: 'New',
        requestDate: new Date().toLocaleDateString('en-CA'),
      }),
    onSuccess: (request) => {
      toast({ title: 'Repair request created' });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      setSelectedEvent(null);
      navigate(`/repair-requests/${request.id}`);
    },
    onError: () => toast({ title: 'Failed to create repair request', variant: 'destructive' }),
  });

  function handleDatesSet(arg: DatesSetArg) {
    setDateRange({
      start: arg.startStr.slice(0, 10),
      end: arg.endStr.slice(0, 10),
    });
  }

  function handleEventClick(info: EventClickArg) {
    const p = info.event.extendedProps as {
      type?: CalendarEventType;
      equipmentId?: number | null;
      assignmentId?: number | null;
      pmDescription?: string | null;
      requestId?: number | null;
      locked?: boolean;
    };

    // Navigate directly to the repair order for: regular repairs, locked pm_due, and pm_done with a request
    if (p.requestId && (p.type === 'repair' || p.locked || p.type === 'pm_done')) {
      navigate(`/repair-requests/${p.requestId}`);
      return;
    }

    if (p.type === 'pm_due' || p.type === 'pm_done') {
      setSelectedEvent({
        id: info.event.id,
        title: info.event.title,
        start: info.event.startStr,
        type: p.type,
        equipmentId: p.equipmentId ?? null,
        assignmentId: p.assignmentId ?? null,
        pmDescription: p.pmDescription ?? null,
        requestId: p.requestId ?? null,
      });
    }
  }

  const today = new Date().toISOString().slice(0, 10);

  const calendarEvents = (events ?? []).map((evt) => {
    const isOverdue = evt.type === 'pm_due' && !evt.locked && !!evt.start && evt.start < today;
    const isRepairDone = evt.type === 'repair' && (evt.status === 'Done' || evt.status === 'Closed');
    const color =
      isOverdue              ? STATE_COLORS.overdue :
      isRepairDone           ? STATE_COLORS.done :
      evt.repair_done        ? STATE_COLORS.done :
      evt.locked             ? STATE_COLORS.locked :
      evt.type === 'pm_done' ? STATE_COLORS.done :
      evt.color ?? (EVENT_COLORS[evt.type as CalendarEventType] ?? '#6b7280');
    const prefix = evt.type === 'repair' ? '🔧 ' : isOverdue ? '⚠ ' : '';
    return {
      id: evt.id,
      title: `${prefix}${evt.title}`,
      start: evt.start,
      end: evt.end,
      backgroundColor: color,
      borderColor: color,
      extendedProps: {
        type: evt.type,
        equipmentId: evt.equipment_id,
        assignmentId: evt.assignment_id,
        pmDescription: evt.pm_description,
        requestId: evt.request_id,
        isOverdue,
        locked: evt.locked ?? false,
        repairDone: evt.repair_done ?? false,
      },
    };
  });

  const isDone = selectedEvent?.type === 'pm_done';
  const hasRequest = !!selectedEvent?.requestId;

  return (
    <PageWrapper title="Calendar" subtitle="Repair requests and PM schedules overview">
      {/* Legend */}
      <div className="mb-4 flex flex-wrap gap-4 text-sm">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: '#c9a84c' }} />
          🔧 Repair Requests
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-full bg-orange-500" />
          PM Due
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-full bg-indigo-500" />
          🔒 Repair Order Created
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-full bg-green-500" />
          PM Completed
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-full bg-red-500" />
          PM Overdue
        </span>
      </div>

      <Card>
        <CardContent className="p-4">
          {isLoading && (
            <div className="mb-4 h-1 w-full animate-pulse rounded bg-primary/30" />
          )}
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, listPlugin]}
            initialView={isMobile ? 'listWeek' : 'dayGridMonth'}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: isMobile ? 'listWeek,listMonth' : 'dayGridMonth,dayGridWeek,listWeek',
            }}
            events={calendarEvents}
            datesSet={handleDatesSet}
            eventClick={handleEventClick}
            height="auto"
            eventDisplay="block"
            dayMaxEvents={false}
            moreLinkClick="popover"
            nowIndicator
          />
        </CardContent>
      </Card>

      {/* PM event detail dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={(open) => { if (!open) setSelectedEvent(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEvent?.title}
              <Badge variant={isDone ? 'default' : 'secondary'}>
                {isDone ? 'Completed' : 'Due'}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-1 text-sm">
            <div>
              <span className="text-muted-foreground">Date: </span>
              {selectedEvent?.start}
            </div>

            {selectedEvent?.pmDescription && (
              <div>
                <p className="font-medium mb-1">Description</p>
                <p className="text-muted-foreground">{selectedEvent.pmDescription}</p>
              </div>
            )}

            {!selectedEvent?.pmDescription && (
              <p className="text-muted-foreground italic">No description on file for this PM type.</p>
            )}

            {hasRequest && (
              <div className="rounded-md bg-green-50 dark:bg-green-900/20 px-3 py-2 text-green-700 dark:text-green-400 text-sm">
                A repair request has already been created for this PM.
                <Button
                  variant="link"
                  className="h-auto p-0 ml-1 text-green-700 dark:text-green-400"
                  onClick={() => {
                    setSelectedEvent(null);
                    navigate(`/repair-requests/${selectedEvent?.requestId}`);
                  }}
                >
                  View it →
                </Button>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSelectedEvent(null)}>
              Close
            </Button>
            {!isDone && !hasRequest && selectedEvent?.equipmentId && (
              <Button
                onClick={() => createRequestMutation.mutate(selectedEvent.equipmentId!)}
                disabled={createRequestMutation.isPending}
              >
                {createRequestMutation.isPending ? 'Creating…' : 'Create Repair Request'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageWrapper>
  );
}
